import { METER_CONTENT } from '../content/meter-content';
import { useSelector, useSetUsageType } from '../context/hooks';
import {
	getNumberFormatLocale,
	selectEstimatedUsageRange,
	selectMeter,
	selectUsage,
	selectUsageType,
} from '../context/selectors';

import { ResultsCard } from './results-card';
import { METER_LABELS, formatNumberRange } from './strings';

/**
 * "Your Organization Needs" card — shared by the Estimate step (Step 3)
 * and the Summary step (Step 5). Both steps render an identical card
 * (usage-range headline + usage-type dropdown + value-prop footer), so
 * the wiring lives here once and each step passes only a `testId`.
 *
 * Content-driven: eyebrow/subtext come from the meter's
 * `summaryStep.orgNeedsCard` (single source of truth). Renders nothing
 * when the meter omits that content.
 */
export const OrgNeedsResultsCard = ({ testId }: { testId: string }) => {
	const meter = useSelector(selectMeter);
	const usageType = useSelector(selectUsageType);
	const usage = useSelector(selectUsage);
	const setUsageType = useSetUsageType();
	const { low, high } = useSelector(selectEstimatedUsageRange);
	const locale = useSelector(getNumberFormatLocale);
	const orgNeedsCard = METER_CONTENT[meter].summaryStep.orgNeedsCard;
	if (!orgNeedsCard) {
		return null;
	}
	// A custom usage value overrides the usageType estimate, so the
	// profile-driven controls (usage-type dropdown + value-prop footer)
	// no longer apply — hide both when a custom value is set.
	const hasCustomUsage = usage > 0;
	// Value-prop footer tracks the user's selected usage type from the
	// Needs step (Exploring / Adopting / Scaling). Switching the dropdown
	// here drives the same `usageType` context state, so the card content
	// stays in sync across steps.
	const footerText = METER_CONTENT[meter].usageProfiles[usageType].valueProp;
	return (
		<ResultsCard
			eyebrow={orgNeedsCard.eyebrow}
			headline={formatNumberRange(low, high, locale)}
			headlineSubtext={orgNeedsCard.headlineSubtext ?? `${METER_LABELS[meter]} per month`}
			// coverageText + bullets intentionally omitted per the 2026-07-17
			// design revision: the bold callout and duplicated bullet list were
			// removed for the OrgNeedsCard. The Needs-step ChoiceCard remains the
			// source of truth for `usageProfile.list`.
			footerNote={hasCustomUsage ? undefined : footerText}
			dropdown={
				hasCustomUsage
					? undefined
					: {
							ariaLabel: 'Meter',
							// Source dropdown labels from the same meter-content registry that
							// powers the Needs step's usage-profile cards, so all surfaces stay
							// in sync per meter. Enum values stay meter-agnostic; only the
							// human-readable labels are per-meter.
							options: (['exploring', 'adopting', 'scaling'] as const).map((profileKey) => ({
								value: profileKey,
								label: METER_CONTENT[meter].usageProfiles[profileKey].label,
							})),
							value: usageType,
							onChange: (value) => setUsageType(value as typeof usageType),
						}
			}
			testId={testId}
		/>
	);
};
